import { useRef, useState, useEffect, ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface HorizontalScrollContainerProps {
  children: ReactNode;
  className?: string;
  showArrows?: boolean;
  snapEnabled?: boolean;
  gap?: string;
}

export function HorizontalScrollContainer({
  children,
  className,
  showArrows = true,
  snapEnabled = true,
  gap = 'gap-6'
}: HorizontalScrollContainerProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const isScrollable = scrollWidth > clientWidth;
    
    // Show left gradient/arrow when scrolled right
    const hasScrolledRight = scrollLeft > 10;
    setShowLeftGradient(isScrollable && hasScrolledRight);
    setShowLeftArrow(isScrollable && hasScrolledRight);
    
    // Show right gradient/arrow when there's more content to the right
    const hasMoreContent = scrollLeft < scrollWidth - clientWidth - 10;
    setShowRightGradient(isScrollable && hasMoreContent);
    setShowRightArrow(isScrollable && hasMoreContent);
  };

  useEffect(() => {
    checkScroll();
    
    const container = scrollContainerRef.current;
    if (!container) return;

    // Check on mount and when content changes
    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(container);

    // Check on scroll
    container.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);

    return () => {
      resizeObserver.disconnect();
      container.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [children]);

  const scrollBy = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = container.clientWidth * 0.8; // Scroll 80% of container width
    const newScrollLeft = direction === 'left' 
      ? container.scrollLeft - scrollAmount
      : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    });
  };

  return (
    <div className="relative">
      {/* Left Gradient */}
      <div
        className={cn(
          "absolute left-0 top-0 bottom-0 w-24 pointer-events-none z-10 transition-opacity duration-300",
          "bg-gradient-to-r from-white dark:from-gray-950 to-transparent",
          showLeftGradient ? "opacity-100" : "opacity-0"
        )}
      />

      {/* Right Gradient */}
      <div
        className={cn(
          "absolute right-0 top-0 bottom-0 w-24 pointer-events-none z-10 transition-opacity duration-300",
          "bg-gradient-to-l from-white dark:from-gray-950 to-transparent",
          showRightGradient ? "opacity-100" : "opacity-0"
        )}
      />

      {/* Left Arrow */}
      {showArrows && (
        <Button
          variant="outline"
          size="icon"
          className={cn(
            "absolute left-2 top-1/2 -translate-y-1/2 z-20 transition-all duration-300 shadow-lg",
            "bg-white dark:bg-gray-950 hover:bg-gray-50 dark:hover:bg-gray-900",
            showLeftArrow ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          )}
          onClick={() => scrollBy('left')}
          data-testid="scroll-left-button"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      )}

      {/* Right Arrow */}
      {showArrows && (
        <Button
          variant="outline"
          size="icon"
          className={cn(
            "absolute right-2 top-1/2 -translate-y-1/2 z-20 transition-all duration-300 shadow-lg",
            "bg-white dark:bg-gray-950 hover:bg-gray-50 dark:hover:bg-gray-900",
            showRightArrow ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          )}
          onClick={() => scrollBy('right')}
          data-testid="scroll-right-button"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      )}

      {/* Scrollable Content */}
      <div
        ref={scrollContainerRef}
        className={cn(
          "overflow-x-auto pb-4",
          snapEnabled && "scroll-smooth snap-x snap-mandatory",
          // Hide scrollbar on most browsers while keeping scroll functionality
          "[&::-webkit-scrollbar]:h-2",
          "[&::-webkit-scrollbar-track]:bg-gray-100 dark:[&::-webkit-scrollbar-track]:bg-gray-800",
          "[&::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600",
          "[&::-webkit-scrollbar-thumb]:rounded-full",
          "[&::-webkit-scrollbar-thumb]:hover:bg-gray-400 dark:[&::-webkit-scrollbar-thumb]:hover:bg-gray-500",
          className
        )}
      >
        <div className={cn("flex", gap, snapEnabled && "[&>*]:snap-start")}>
          {children}
        </div>
      </div>
    </div>
  );
}
